import React, { useMemo, useState, useEffect } from 'react';
import ElementRenderer from './ElementRenderer';
import { getSpreadDimensions } from '../../../BookDesign/constants';

export default function PreviewPage({
  page,
  book,
  scale = 1,
  messengerStyle = false,
  showIntroPage = false,
}) {
  const [pageElements, setPageElements] = useState([]);

  // Extract page content based on page number
  useEffect(() => {
    console.log('%c PreviewPage Component', 'background: #222; color: #bada55; padding: 5px; font-size: 16px;');
    console.log('Page:', page);
    console.log('Book structure:', {
      id: book?.id,
      title: book?.title,
      totalPages: book?.totalPages,
      hasSpreadData: !!book?.spreads,
      spreadKeys: book?.spreads ? Object.keys(book.spreads) : []
    });
    console.log('MessengerStyle:', messengerStyle);

    if (!book || typeof page !== 'number') {
      console.warn('Missing book or page data');
      setPageElements([]);
      return;
    }

    let elements = [];

    // Handle front and back covers
    if (page === 0) {
      // Front cover
      if (book.coverElements) {
        // Direct coverElements array
        elements = book.coverElements || [];
        console.log('Found coverElements array:', elements.length);
      } else if (book.spreads && book.spreads['spread-0']) {
        // Elements may be stored on either side depending on earlier logic
        const left = book.spreads['spread-0'].leftPageElements || [];
        const right = book.spreads['spread-0'].rightPageElements || [];
        // Prefer whichever side has content; front cover is usually on the right
        elements = right.length > 0 ? right : left;
        console.log('Using spread-0 cover elements (right preferred):', elements.length);
      }
    } else if (!showIntroPage && page === parseInt(book.totalPages, 10) + 1) {
      // Back cover
      if (book.backCoverElements) {
        elements = book.backCoverElements || [];
        console.log('Found backCoverElements array:', elements.length);
      } else if (book.spreads && book.spreads['spread-0']) {
        // Elements may be in spread-0 rightPageElements in some data shapes
        elements = book.spreads['spread-0'].rightPageElements || [];
        console.log('Using spread-0 rightPageElements for back cover:', elements.length);
      }
    } else {
      // Content pages
      const effectivePage = (showIntroPage && page >= 2) ? (page - 1) : page;

      const spreadIndex = Math.ceil(effectivePage / 2);
      const spreadKey = `spread-${spreadIndex}`;
      let spread = book.spreads?.[spreadKey];
      if (!spread && book?.spreads && typeof book.spreads === 'object') {
        const allSpreads = Object.values(book.spreads);
        const found = allSpreads.find((s) => s && (s.id === spreadKey));
        if (found) spread = found;
      }
      
      if (spread) {
        // Determine page side (left or right)
        // In our layout: odd pages are LEFT, even pages are RIGHT.
        const isRightPage = effectivePage % 2 === 0;

        // Prefer unified elements array and split by X; fall back to explicit left/right arrays
        if (Array.isArray(spread.elements) && spread.elements.length > 0) {
          const dims2 = getSpreadDimensions(book?.pageSize || 'us-trade');
          const pageWidth2 = dims2.width;

          const filtered = spread.elements.filter(el => {
            // Get element position and size
            const elementLeft = el?.position?.x ?? 0;
            const elementRight = elementLeft + (el?.size?.width ?? 0);
            
            // For right pages, include elements that extend past the page boundary (spans into right page)
            if (isRightPage) {
              return elementRight > pageWidth2;
            }
            
            // For left pages, include elements that start before the page boundary (includes spanning elements)
            return elementLeft < pageWidth2;
          });

          // Translate right-page elements into a single-page coordinate system
          elements = filtered.map(el => {
            if (!isRightPage) return el;
            const x = (el?.position?.x ?? 0) - pageWidth2;
            return { ...el, position: { ...el.position, x } };
          });
          console.log(`Split spread.elements from ${spreadKey} for preview page ${page} (effective content page ${effectivePage}):`, elements.length);

          // If split produced no elements (edge case), try fallback arrays
          if (elements.length === 0) {
            if (isRightPage && Array.isArray(spread.rightPageElements)) {
              elements = spread.rightPageElements;
              console.log(`Fallback to rightPageElements from ${spreadKey} for preview page ${page}:`, elements.length);
            } else if (!isRightPage && Array.isArray(spread.leftPageElements)) {
              elements = spread.leftPageElements;
              console.log(`Fallback to leftPageElements from ${spreadKey} for preview page ${page}:`, elements.length);
            }
          }
        } else if (isRightPage && Array.isArray(spread.rightPageElements)) {
          // Assume right/left arrays already use per-page coordinates
          elements = spread.rightPageElements;
          console.log(`Using rightPageElements from ${spreadKey} for preview page ${page} (effective ${effectivePage}):`, elements.length);
        } else if (!isRightPage && Array.isArray(spread.leftPageElements)) {
          elements = spread.leftPageElements;
          console.log(`Using leftPageElements from ${spreadKey} for preview page ${page} (effective ${effectivePage}):`, elements.length);
        }
      } else {
        console.warn(`Spread ${spreadKey} not found for preview page ${page}`);
      }
    }

    // Update state with resolved elements
    setPageElements(elements);
  }, [page, book, messengerStyle]);

  const summaryElement = useMemo(() => {
    if (!messengerStyle || pageElements.length === 0) return null;

    // Get all text elements and combine their content
    const textElements = pageElements.filter(el => el.type === 'text');
    
    if (textElements.length === 0) {
      console.log('No text elements found for messenger style');
      return {
        type: 'text',
        content: 'No text content on this page.',
        position: { x: 0, y: 0 },
        size: { width: '100%', height: 'auto' },
        style: {
          fontSize: '16px',
          fontFamily: 'Arial, sans-serif',
          color: '#000000',
        },
      };
    }
    
    const combinedContent = textElements.map(el => el.content).join('\n\n');
    console.log('Created summary element with content:', combinedContent.substring(0, 50) + '...');
    
    // Create a new summary element
    return {
      type: 'text',
      content: combinedContent,
      position: { x: 0, y: 0 },
      size: { width: '100%', height: 'auto' },
      style: {
        fontSize: '16px',
        fontFamily: 'Arial, sans-serif',
        color: '#000000',
      },
    };
  }, [pageElements, messengerStyle]);

  // If using messenger style and we have a summary element, render that
  if (messengerStyle) {
    console.log('Rendering in messenger style with summary element');
    return (
      <div style={{ 
        position: 'relative',
        width: '100%',
        display: 'block',
        height: 'auto',
        minHeight: '100px',
      }}>
        {summaryElement ? (
          <ElementRenderer 
            element={summaryElement}
            scale={scale}
            messengerStyle={true}
          />
        ) : (
          <div style={{ 
            padding: '12px',
            color: '#65676B',
            fontStyle: 'italic',
            textAlign: 'center'
          }}>
            No content available for this page.
          </div>
        )}
      </div>
    );
  }

  // Determine page dimensions based on book size
  const dims = getSpreadDimensions(book?.pageSize || 'us-trade');
  const pageWidthPx = dims.width; // single page
  const pageHeightPx = dims.height;

  // Synthetic Introduction page (preview-only)
  if (!messengerStyle && showIntroPage && page === 1) {
    const title = (book?.title || 'Title').toString();
    return (
      <div 
        className="preview-page"
        style={{ 
          position: 'relative',
          width: `${pageWidthPx * scale}px`,
          height: `${pageHeightPx * scale}px`,
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Top spacer */}
        <div style={{ height: 0 }} />
        {/* Title */}
        <div style={{
          fontFamily: 'Baskervville, serif',
          fontSize: `${Math.max(18, Math.floor(36 * scale))}px`,
          color: '#000',
          marginTop: `${pageHeightPx * scale * 0.25}px`,
          textAlign: 'center',
          maxWidth: `${pageWidthPx * scale * 0.72}px`,
          whiteSpace: 'normal',
          overflowWrap: 'break-word',
        }}>
          {title}
        </div>
        <div style={{ flex: 1 }} />
        {/* Bottom footer */}
        <div style={{
          width: '100%',
          textAlign: 'center',
          marginBottom: `${100 * scale}px`,
          color: '#777',
          fontSize: `${Math.max(10, Math.floor(12 * scale))}px`,
        }}>
          <div>Created With</div>
          <div>Storybook Wizard</div>
        </div>
      </div>
    );
  }

  // Otherwise render the regular layout
  // Sort elements by z-index to ensure proper layering (lowest z-index first = behind)
  const sortedElements = useMemo(() => {
    return [...pageElements].sort((a, b) => {
      const zIndexA = a.zIndex ?? 0;
      const zIndexB = b.zIndex ?? 0;
      return zIndexA - zIndexB;
    });
  }, [pageElements]);

  return (
    <div 
      className="preview-page"
      style={{ 
        position: 'relative',
        width: `${pageWidthPx * scale}px`,
        height: `${pageHeightPx * scale}px`,
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      {sortedElements.length > 0 ? (
        sortedElements.map((element, index) => (
          <ElementRenderer 
            key={`element-${index}`}
            element={element}
            scale={scale}
            messengerStyle={false}
          />
        ))
      ) : (
        <div style={{ 
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)',
          color: '#65676B',
          fontStyle: 'italic'
        }}>
          No content available for this page.
        </div>
      )}

      {/* Page number overlay (interior only) */}
      {book?.hasPageNumbers && (() => {
        const total = parseInt(book?.totalPages || 0, 10) || 0;
        const isFrontCover = page === 0;
        const isBackCover = !showIntroPage && page === total + 1;
        const isIntro = showIntroPage && page === 1;
        const effectivePage = (showIntroPage && page >= 2) ? (page - 1) : page;
        const showNumber = !isFrontCover && !isBackCover && !isIntro && effectivePage >= 1 && effectivePage <= total;
        return showNumber ? (
          <div
            className="absolute bottom-2 text-xs text-gray-600 font-medium"
            style={{ left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}
          >
            {effectivePage}
          </div>
        ) : null;
      })()}
    </div>
  );
}
